import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Plus,
  CalendarIcon,
  Briefcase,
  Euro,
  Building2,
  User,
  Edit,
  Trash2,
} from 'lucide-react';

const STATUS_CONFIG = {
  draft: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700' },
  active: { label: 'Active', color: 'bg-emerald-100 text-emerald-700' },
  completed: { label: 'Terminée', color: 'bg-blue-100 text-blue-700' },
  cancelled: { label: 'Annulée', color: 'bg-red-100 text-red-700' },
};

export default function MissionsPage() {
  const { user, isManager } = useAuth();
  const [missions, setMissions] = useState([]);
  const [clients, setClients] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMission, setEditingMission] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    client_id: '',
    consultant_id: '',
    tjm: '',
    start_date: new Date(),
    end_date: null,
    status: 'draft',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [missionsRes, clientsRes, consultantsRes] = await Promise.all([
        api.get('/missions'),
        api.get('/clients'),
        api.get('/consultants'),
      ]);
      setMissions(missionsRes.data);
      setClients(clientsRes.data);
      setConsultants(consultantsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.client_id || !formData.tjm) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const payload = {
        ...formData,
        tjm: parseFloat(formData.tjm),
        start_date: formData.start_date.toISOString(),
        end_date: formData.end_date?.toISOString() || null,
      };

      if (editingMission) {
        await api.put(`/missions/${editingMission.mission_id}`, payload);
        toast.success('Mission mise à jour');
      } else {
        await api.post('/missions', payload);
        toast.success('Mission créée');
      }

      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (missionId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette mission ?')) return;

    try {
      await api.delete(`/missions/${missionId}`);
      toast.success('Mission supprimée');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      client_id: '',
      consultant_id: '',
      tjm: '',
      start_date: new Date(),
      end_date: null,
      status: 'draft',
    });
    setEditingMission(null);
  };

  const openEditDialog = (mission) => {
    setEditingMission(mission);
    setFormData({
      title: mission.title,
      description: mission.description || '',
      client_id: mission.client_id,
      consultant_id: mission.consultant_id || '',
      tjm: mission.tjm.toString(),
      start_date: new Date(mission.start_date),
      end_date: mission.end_date ? new Date(mission.end_date) : null,
      status: mission.status,
    });
    setIsDialogOpen(true);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Missions</h1>
            <p className="text-slate-500 mt-1">
              Gérez les missions et les affectations des consultants
            </p>
          </div>
          {isManager && (
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-slate-900 hover:bg-slate-800" data-testid="create-mission-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvelle mission
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>
                    {editingMission ? 'Modifier la mission' : 'Créer une mission'}
                  </DialogTitle>
                  <DialogDescription>
                    Renseignez les informations de la mission
                  </DialogDescription>
                </DialogHeader>

                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Titre *</Label>
                      <Input
                        id="title"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="Ex: Développement application web"
                        data-testid="mission-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="tjm">TJM (€) *</Label>
                      <Input
                        id="tjm"
                        type="number"
                        value={formData.tjm}
                        onChange={(e) => setFormData({ ...formData, tjm: e.target.value })}
                        placeholder="500"
                        data-testid="mission-tjm-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Client *</Label>
                      <Select
                        value={formData.client_id}
                        onValueChange={(v) => setFormData({ ...formData, client_id: v })}
                      >
                        <SelectTrigger data-testid="mission-client-select">
                          <SelectValue placeholder="Sélectionner un client" />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map((client) => (
                            <SelectItem key={client.client_id} value={client.client_id}>
                              {client.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Consultant</Label>
                      <Select
                        value={formData.consultant_id}
                        onValueChange={(v) => setFormData({ ...formData, consultant_id: v })}
                      >
                        <SelectTrigger data-testid="mission-consultant-select">
                          <SelectValue placeholder="Sélectionner un consultant" />
                        </SelectTrigger>
                        <SelectContent>
                          {consultants.map((consultant) => (
                            <SelectItem key={consultant.consultant_id} value={consultant.consultant_id}>
                              {consultant.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date de début *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="mission-start-date">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formData.start_date ? format(formData.start_date, 'dd/MM/yyyy') : 'Sélectionner'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={formData.start_date}
                            onSelect={(date) => setFormData({ ...formData, start_date: date })}
                            locale={fr}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label>Date de fin</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="mission-end-date">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formData.end_date ? format(formData.end_date, 'dd/MM/yyyy') : 'Non définie'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={formData.end_date}
                            onSelect={(date) => setFormData({ ...formData, end_date: date })}
                            locale={fr}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Statut</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(v) => setFormData({ ...formData, status: v })}
                    >
                      <SelectTrigger data-testid="mission-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Brouillon</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="completed">Terminée</SelectItem>
                        <SelectItem value="cancelled">Annulée</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Description de la mission..."
                      rows={3}
                      data-testid="mission-description-input"
                    />
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Annuler
                  </Button>
                  <Button onClick={handleSubmit} className="bg-slate-900 hover:bg-slate-800" data-testid="save-mission-btn">
                    {editingMission ? 'Mettre à jour' : 'Créer'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Briefcase className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {missions.filter(m => m.status === 'active').length}
                  </p>
                  <p className="text-sm text-slate-500">Missions actives</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {new Set(missions.map(m => m.client_id)).size}
                  </p>
                  <p className="text-sm text-slate-500">Clients</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Euro className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {Math.round(missions.reduce((sum, m) => sum + (m.tjm || 0), 0) / missions.length || 0)} €
                  </p>
                  <p className="text-sm text-slate-500">TJM moyen</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Missions Table */}
        <Card>
          <CardHeader>
            <CardTitle>Liste des missions</CardTitle>
          </CardHeader>
          <CardContent>
            {missions.length === 0 ? (
              <div className="text-center py-12">
                <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Aucune mission pour le moment</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Mission</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Consultant</TableHead>
                      <TableHead className="text-center">TJM</TableHead>
                      <TableHead>Période</TableHead>
                      <TableHead className="text-center">Statut</TableHead>
                      {isManager && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {missions.map((mission) => (
                      <TableRow key={mission.mission_id} data-testid={`mission-row-${mission.mission_id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-slate-900">{mission.title}</p>
                            {mission.description && (
                              <p className="text-sm text-slate-500 truncate max-w-xs">
                                {mission.description}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{mission.client_name}</TableCell>
                        <TableCell>
                          {mission.consultant_name || (
                            <span className="text-slate-400">Non assigné</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center font-mono">
                          {mission.tjm} €
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p>{format(new Date(mission.start_date), 'dd/MM/yyyy')}</p>
                            {mission.end_date && (
                              <p className="text-slate-500">
                                → {format(new Date(mission.end_date), 'dd/MM/yyyy')}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={STATUS_CONFIG[mission.status]?.color}>
                            {STATUS_CONFIG[mission.status]?.label}
                          </Badge>
                        </TableCell>
                        {isManager && (
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEditDialog(mission)}
                                data-testid={`edit-mission-${mission.mission_id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDelete(mission.mission_id)}
                                data-testid={`delete-mission-${mission.mission_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
